<template>
  <div
    ref="scrollContainerRef"
    v-if="!firstRender && rows.length"
    :class="['MultiColumnTable', { dragging: dragScrollState.isDragging }]"
    :style="{ overflow: scrollDisabled ? 'visible' : 'auto' }"
    @scroll="queueSpanningCellContentPositionUpdate"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="onPointerUp"
    @pointercancel="onPointerUp"
    @click.capture="onClickCapture"
    @dragstart.prevent
  >
    <table ref="tableRef">
      <tr v-for="(columns, levelIndex) in stickyColumnsByLevel" :key="levelIndex" class="thead" style="height: 28px;">
        <th v-if="levelIndex == 0" class="tableTitle" :rowSpan="stickyColumnsByLevel.length" :colSpan="maxHeaderLength" >
          <template v-if="showingDesktopUi && showTableName">
            {{ props.tableName ?? table.tableName }}
          </template>
        </th>
        <th v-for="(column, columnIndex) in columns"
          :class="{ firstColumnHeader: columnIndex == 0, spanningColumnHeader: column.colSpan > 1 }"
          :style="{ width: column.width }"
          :colSpan="column.colSpan"
          @click="toggleShowingAllBins()"
        >
          <span class="columnHeaderContent">
            <slot name="columnHeader" :column>
              <template v-if="columns.length > 1">
                {{ column.name }}
              </template>
            </slot>
          </span>
        </th>
      </tr>
      <template v-for="row in rows" :key="row.id">
        <tr v-for="headerRow in getInlineColumnHeaderRows(row)" :key="headerRow.key" class="inlineColumnHeaderRow" :data-header-level="headerRow.levelIndex" style="height: 28px;">
          <th class="rowHeader subColumnSpacer" :colSpan="maxHeaderLength"></th>
          <template v-for="(segment, segmentIndex) in headerRow.segments" :key="`${headerRow.key}-${segmentIndex}`">
            <th v-if="segment.column" :class="['inlineColumnHeader', { spanningColumnHeader: segment.colSpan > 1 }]" :colSpan="segment.colSpan" :style="{ width: segment.column.width }">
              <span class="columnHeaderContent">
                <slot name="columnHeader" :column="segment.column">
                  <template v-if="headerRow.columnCount > 1">
                    {{ segment.column.name }}
                  </template>
                </slot>
              </span>
            </th>
            <td v-else class="inlineColumnHeaderSpacer" :colSpan="segment.colSpan"></td>
          </template>
        </tr>
        <tr class="tbody">
          <th v-for="header in row.headers" 
            :rowspan="header.rowspan" 
            :colspan="header.colspan"
            :class="['rowHeader', header.cssClasses]"
          >
            {{ header.text }}
          </th>
          <template v-for="displayCell in getDisplayCells(row)" :key="`${row.id}-${displayCell.startIndex}`">
            <template v-if="displayCell.cell == null">
              <td :colspan="displayCell.colspan"></td>
            </template>
            <template v-else>
              <component :class="{ relevant: true, spanningCell: displayCell.colspan > 1 }" v-bind="displayCell.cell.componentArgs"
                :is="displayCell.cell.componentType"
                :colspan="displayCell.colspan"
                :row="row"
                :cell="displayCell.cell"
                :columnTag="displayCell.cell.columnTag"
                :selectedCells="selectedCellsOrNull(row)"
                :showingPopover="isShowingPopover(row, displayCell.startIndex)"
                :applyToAllColumnsEnabled="cellApplyToAllColumnsEnabled(row, displayCell.cell)"
                @update:showingPopover="value => setShowingPopover(row, displayCell.startIndex, value)"
                v-model:columnSelectionCode="columnSelectionCode"
              >
                <template #BinSelectionInput>
                  <BinSelectionInput
                    :tag="displayCell.cell.componentArgs.tag"
                    :applyToAllColumnsEnabled="cellApplyToAllColumnsEnabled(row, displayCell.cell)"
                    v-model:columnSelectionCode="columnSelectionCode"
                  ></BinSelectionInput>
                </template>
              </component>
            </template>
          </template>
        </tr>
      </template>
    </table>
  </div>
</template>

<script setup>
import { computed, reactive, ref, onBeforeUnmount, onMounted, watch } from "vue";
import { promiseTimeout } from "@vueuse/core";
import store, { showingDesktopUi } from "./store.js";
import { chunk, hasMatchingItemsBy, groupBy, map_dropNulls, sortBy, sumBy } from "./utility.js";
import { resolveTable, combineTables } from "./TableUtility.js";

let props = defineProps({
  tableName: {},
  tableData: {},
  tableKey: {},
  scrollDisabled: { type: Boolean, default: false },
  showTableName: { type: Boolean, default: true },
  showSystemColumnsWhenMerged: { type: Boolean, default: true },
  disableFirstRenderDelay: { type: Boolean, default: false }
});

let scrollContainerRef = ref();
const dragScrollThreshold = 6;
let dragScrollState = reactive({
  pointerId: null,
  startX: 0,
  startY: 0,
  startScrollLeft: 0,
  startScrollTop: 0,
  hasExceededThreshold: false,
  isDragging: false,
  suppressClick: false
});

let firstRender = ref(props.disableFirstRenderDelay ? false : true);
onMounted(async () => {
  await promiseTimeout(10);
  firstRender.value = false;
});

let table = computed(() => {
  if (props.tableData) {
    return props.tableData;
  }
  else {
    let connectedSessions = store.sessions.filter(session => session.isConnected);
    let tables = connectedSessions.map(session => {
      let tableData = session.system.tables[props.tableKey].value;
      let table = resolveTable(session, session.system, tableData);
      return table;
    });
    let combinedTable = combineTables(tables, props.showSystemColumnsWhenMerged);
    return combinedTable;
  }
});

let columnsByLevel = computed(() => {
  let columnsByLevel = [];
  let traversalQueue = [ ... table.value.columns ];
  while (traversalQueue.length > 0) {
    let levelSize = traversalQueue.length;
    let levelColumns = [];
    for (let i = 0; i < levelSize; i++) {
      let column = traversalQueue.shift();
      levelColumns.push(column);
      if (column.subColumns) {
        column.subColumns.forEach(subColumn => {
          traversalQueue.push(subColumn);
        });
      }
    }
    columnsByLevel.push(levelColumns);
  }
  return columnsByLevel;
});

let stickyColumnsByLevel = computed(() => columnsByLevel.value.slice(0, 1));

function getBottomLevelColumns(columns) {
  let subColumns = columns.flatMap(column => column.subColumns ?? []);
  while (subColumns.length > 0) {
    columns = subColumns;
    subColumns = subColumns.flatMap(column => column.subColumns ?? []);
  }
  return columns;
}

function tagIsInColumnSubtree(tag, columnTag) {
  return tag === columnTag || tag.$parents().includes(columnTag);
}

function rowHasCellInColumnSubtree(row, column) {
  return row.cells.some(cell => {
    let tag = cell?.componentArgs?.tag ?? cell?.tag;
    return tag != null && tagIsInColumnSubtree(tag, column.tag);
  });
}

let inlineColumnHeaderRowsByRowId = computed(() => {
  let headerRowsByRowId = {};

  columnsByLevel.value.slice(1).forEach((columns, levelOffset) => {
    if (columns.length <= 1) {
      return;
    }

    let firstMatchingRowIndex = rows.value.findIndex(row => {
      return columns.some(column => rowHasCellInColumnSubtree(row, column));
    });
    if (firstMatchingRowIndex == -1) {
      return;
    }

    let segments = columns.map(column => ({
      colSpan: column.colSpan,
      column
    }));
    let row = rows.value[firstMatchingRowIndex];
    (headerRowsByRowId[row.id] ??= []).push({
      key: `${row.id}-column-header-level-${levelOffset + 1}`,
      levelIndex: levelOffset + 1,
      columnCount: columns.length,
      segments
    });
  });

  return headerRowsByRowId;
});

function getInlineColumnHeaderRows(row) {
  return inlineColumnHeaderRowsByRowId.value[row.id] ?? [];
}

let rows = computed(() => 
{
  // Rename the rows if necessary, and parse the headers.
  let rows = table.value.rows.map(row => {
    let rowName = row.rowName;
    let headersSplit = rowName.split(/(\|+)/);
    let headerChunks = chunk(headersSplit, 2);
    let headers = headerChunks.map(chunk => {
      return {
        text: chunk[0].trim(),
        forceSplit: chunk[1] == "||",
        comparisonValue: chunk.join()
      }
    })
    return {
      ... row,
      rowName,
      headers
    };
  });

  // Shorten row headers like the following
  //
  //    Auxiliary 1 | ?Temperature | Setting X
  //    Auxiliary 1 | ?Temperature | Setting Y
  //
  // into 
  //
  //    Auxiliary 1 | Setting X
  //    Auxiliary 1 | Setting Y
  //
  // if there are no other aggregates under "Auxiliary 1" in any single column.
  //
  rows.forEach(row => {
    row.headers = row.headers.filter((header, index) => {
      if (header.text.startsWith("?")) {
        let precedingHeaders = row.headers.slice(0, index);
        let rowStartsWith = (row, headers) => headers.every((header, i) => header.comparisonValue == row.headers[i].comparisonValue);
        let group = rows.filter(row => rowStartsWith(row, precedingHeaders));
        let groupHasAtLeastTwoRowsWithSameColumn = hasMatchingItemsBy(group, (row1, row2) => {
          let bothRowsHaveAtLeastOneSameColumn = row1.cells.some((cell, index) => cell != null && row2.cells[index] != null);
          return bothRowsHaveAtLeastOneSameColumn;
        });
        return groupHasAtLeastTwoRowsWithSameColumn;
      }
      else {
        return true;
      }
    });
  });

  // Shorten row headers like "Supply | Temperature" into "Supply temperature" when there are no other aggregates under "Supply".
  // Do not shorten row headers that use a doubled separator, as in "Supply || Temperature".
  rows.forEach(row => {
    for (let i = row.headers.length - 1; i >= 1; i--) {
      let header = row.headers[i];
      let isLastHeader = (i == row.headers.length - 1);
      let precedingHeaders = row.headers.slice(0, i).map(header => header.comparisonValue).join();
      let hasSiblingRows = rows.some(other => {
        if (row == other) return false;
        if (header.comparisonValue == other.headers[i]?.comparisonValue && !isLastHeader) return false; // Preceding headers must match, but the header itself must not match (unless it's the last header).
        let other_precedingHeaders = other.headers.slice(0, i).map(header => header.comparisonValue).join();
        let bothRowsHaveSamePrecedingHeaders = (precedingHeaders == other_precedingHeaders);
        return bothRowsHaveSamePrecedingHeaders;
      });
      
      let precedingHeader = row.headers[i - 1];
      let canMergeHeaderToLeft = !hasSiblingRows && !precedingHeader.forceSplit;
      if (canMergeHeaderToLeft) {
        row.headers[i - 1].text += " " + header.text.toLowerCase();
        row.headers.pop();
      }
    }
  });

  // Reorder and group rows based on their header text.
  // For example
  //    Aux 1 | Setting X
  //    Aux 2 | Setting X
  //    Aux 1 | Setting Y
  //    Aux 2 | Setting Y
  // should become
  //    Aux 1 | Setting X
  //    Aux 1 | Setting Y
  //    Aux 2 | Setting X
  //    Aux 2 | Setting Y
  function groupRows(rows, headerColumnIndex) {
    if (rows.length == 0) return rows;
    if (rows[0].headers.length <= headerColumnIndex) return rows;
    let groups = groupBy(rows, row => row.headers[headerColumnIndex].comparisonValue);
    let headersValues = sortBy(Object.keys(groups), headerValue => {
      return rows.findIndex(row => row.headers[headerColumnIndex].comparisonValue == headerValue);
    });
    let _rows = [];
    headersValues.forEach(headerValue => {
      let group = groups[headerValue];
      _rows.push( ... groupRows(group, headerColumnIndex + 1));
    })
    return _rows;
  }
  rows = groupRows(rows, 0);

  // Convert headers to { text, rowspan, colspan }.
  let headerLengths = rows.map(row => row.headers.length);
  let maxHeaderLength = Math.max(... headerLengths);
  rows = rows.map((row, rowIndex) => {
    let prevRow = (rowIndex > 0) ? rows[rowIndex - 1] : null;
    return {
      ... row,
      headers: row.headers.map((header, headerIndex) => {
        let joinLeadingHeaders = (targetRow) => targetRow?.headers.slice(0, headerIndex + 1).map(header => header.comparisonValue).join();
        let isCoveredHeader = joinLeadingHeaders(prevRow) == joinLeadingHeaders(row);
        if (isCoveredHeader && showingDesktopUi.value) return null;
        let isLastHeader = (headerIndex == row.headers.length - 1);
        let childRows = rows.slice(rowIndex + 1).filter(other => joinLeadingHeaders(other) == joinLeadingHeaders(row));
        let rowspan = 1 + childRows.length;
        let colspan = (isLastHeader) ? maxHeaderLength - headerIndex : 1;
        return {
          text: header.text.replace("?", ""),
          rowspan,
          colspan
        };
      })
    };
  });
  
  // On mobile, combine the row headers into a single {rowSpan: 1, colSpan: 1} header.
  if (!showingDesktopUi.value) {
    rows.forEach(row => {
      row.headers = [{
        rowspan: 1,
        colspan: 1,
        text: row.headers.map((header, index) => index ? header.text.toLowerCase() : header.text).join(" ")
      }]
    });
  }

  // Filter out null headers.
  rows = rows.map(row => ({
    ... row,
    headers: row.headers.filter(header => header != null)
  }));

  let rowWithBottomLeftHeader = rows.findLast(row => {
    let headersColspan = 0;
    row.headers.forEach(header => headersColspan += header.colspan);
    return headersColspan == maxHeaderLength;
  });
  if (rowWithBottomLeftHeader) {
    rowWithBottomLeftHeader.headers[0].cssClasses = [
      "bottomLeft"
    ];
  }

  return rows;
});

let maxHeaderLength = computed(() => {
  let headerLengths = rows.value.map(row => sumBy(row.headers, header => header.colspan));
  return Math.max(... headerLengths);
});

let columnSelectionCode = ref("selectedColumn");

let selectedRowId = ref(null);
let selectedRow = computed(() => rows.value.find(row => row.id == selectedRowId.value));

let selectedCellIndex = ref(null);
let selectedCell = computed(() => {
  return selectedRow.value?.cells[selectedCellIndex.value];
});

let selectedCells = computed(() => {
  let row = rows.value.find(row => row.id == selectedRowId.value);
  if (row == null) return [];
  
  let cell = row.cells[selectedCellIndex.value];
  if (cell == null) {
    return null;
  }
  else if (columnSelectionCode.value == "selectedColumn") {
    return [ cell ];
  }
  else if (columnSelectionCode.value == "allColumns") {
    return row.cells.filter(cell => cell != null);
  }
  else {
    let sessions = store.sessions.filter(session => session.system != null);
    let allBins = sessions.flatMap(session => session.system.bins.children);
    let selectedBins = allBins.filter(bin => {
      let system = bin.$session.system;
      return (bin.binGroups.value.includes(columnSelectionCode.value) || system.boardName.value == columnSelectionCode.value);
    });
    return row.cells.filter(cell => {
      let bin = cell?.componentArgs.tag.$parents().find(parent => allBins.includes(parent));
      return selectedBins.includes(bin);
    });
  }
});

// Reset the columns selection when 
//  1) Selecting a column outside of the previous selection, or
//  2) Selecting a cell for which applyToAllColumnsEnabled is false.
watch(selectedCell, () => {
  if (selectedCell.value != null) {
    let selectedCellIndexes = selectedCells.value.map(cell => selectedRow.value.cells.indexOf(cell));
    let shouldResetColumnSelections = (
      !selectedCellIndexes.includes(selectedCellIndex.value) ||
      !cellApplyToAllColumnsEnabled(selectedRow.value, selectedCell.value)
    );
    if (shouldResetColumnSelections)
      columnSelectionCode.value = "selectedColumn";
  }
});

// Prevents triggering unecessary re-renders of all cell components when
// selectedCells changes, by returning null for rows that aren't selected.
function selectedCellsOrNull(row) {
  if (row.id == selectedRowId.value) {
    return selectedCells.value;
  }
  else {
    return null;
  }
}

function toggleShowingAllBins() {
  store.showingAllBinsPreference = !store.showingAllBinsPreference;
}

function resetDragScrollState() {
  dragScrollState.pointerId = null;
  dragScrollState.startX = 0;
  dragScrollState.startY = 0;
  dragScrollState.startScrollLeft = 0;
  dragScrollState.startScrollTop = 0;
  dragScrollState.hasExceededThreshold = false;
  dragScrollState.isDragging = false;
}

function onPointerDown(event) {
  if (props.scrollDisabled || event.button !== 0)
    return;

  dragScrollState.suppressClick = false;
  dragScrollState.pointerId = event.pointerId;
  dragScrollState.startX = event.clientX;
  dragScrollState.startY = event.clientY;
  dragScrollState.startScrollLeft = scrollContainerRef.value?.scrollLeft ?? 0;
  dragScrollState.startScrollTop = scrollContainerRef.value?.scrollTop ?? 0;
  dragScrollState.hasExceededThreshold = false;
  dragScrollState.isDragging = false;
}

function onPointerMove(event) {
  if (dragScrollState.pointerId !== event.pointerId || scrollContainerRef.value == null)
    return;

  let deltaX = event.clientX - dragScrollState.startX;
  let deltaY = event.clientY - dragScrollState.startY;
  if (!dragScrollState.hasExceededThreshold) {
    let dragDistance = Math.hypot(deltaX, deltaY);
    if (dragDistance < dragScrollThreshold)
      return;

    dragScrollState.hasExceededThreshold = true;
    dragScrollState.isDragging = true;
    dragScrollState.suppressClick = true;
    scrollContainerRef.value.setPointerCapture(event.pointerId);
  }

  event.preventDefault();
  scrollContainerRef.value.scrollLeft = dragScrollState.startScrollLeft - deltaX;
  scrollContainerRef.value.scrollTop = dragScrollState.startScrollTop - deltaY;
}

function onPointerUp(event) {
  if (dragScrollState.pointerId !== event.pointerId || scrollContainerRef.value == null)
    return;

  if (scrollContainerRef.value.hasPointerCapture(event.pointerId)) {
    scrollContainerRef.value.releasePointerCapture(event.pointerId);
  }

  resetDragScrollState();
}

function onClickCapture(event) {
  if (!dragScrollState.suppressClick)
    return;

  event.preventDefault();
  event.stopPropagation();
  dragScrollState.suppressClick = false;
}

function cellApplyToAllColumnsEnabled(row, cell) {
  let relevantCells = row.cells.filter(cell => cell != null);
  return (
    relevantCells.length > 1 &&
    cell.tag.applyToAllColumnsEnabled?.value !== false &&
    cell.componentArgs.applyToAllColumnsEnabled !== false
  );
}

function isShowingPopover(row, cellIndex) {
  return (
    row.id == selectedRowId.value && 
    cellIndex == selectedCellIndex.value
  );
}

function setShowingPopover(row, cellIndex, value) {
  if (value) {
    selectedRowId.value = row.id;
    selectedCellIndex.value = cellIndex;
  }
  else if (isShowingPopover(row, cellIndex)) {
    selectedRowId.value = null;
    selectedCellIndex.value = null;
  }
}

function shouldMergeCells(leftCell, rightCell) {
  if (leftCell == null || rightCell == null)
    return false;

  // Merge only repeated instances of the same resolved cell/tag across adjacent columns.
  return (
    leftCell?.cellId == rightCell?.cellId &&
    leftCell?.componentType == rightCell?.componentType
  );
}

function getDisplayCells(row) {
  let displayCells = [];
  let startIndex = 0;
  while (startIndex < row.cells.length) {
    let cell = row.cells[startIndex];
    let colspan = 1;
    while (startIndex + colspan < row.cells.length) {
      let nextCell = row.cells[startIndex + colspan];
      if (!shouldMergeCells(cell, nextCell))
        break;
      colspan++;
    }
    displayCells.push({
      cell,
      startIndex,
      colspan
    });
    startIndex += colspan;
  }
  return displayCells;
}

// Horizontally position the table body <th> elements so the table can be horizontally scrolled.
// Normally, it is sufficient to simply specify 'position: sticky', but this fails when there are
// <th> elements spanning several rows or columns.
let tableRef = ref();
let spanningCellPositionAnimationFrame = null;
let spanningCellResizeObserver = null;

function getSpanningCells() {
  if (tableRef.value == null)
    return [];

  return [
    ...tableRef.value.querySelectorAll("td.spanningCell"),
    ...tableRef.value.querySelectorAll("th.spanningColumnHeader")
  ];
}

function resetSpanningCellObservers() {
  spanningCellResizeObserver?.disconnect();

  if (tableRef.value == null)
    return;

  spanningCellResizeObserver = new ResizeObserver(() => {
    queueSpanningCellContentPositionUpdate();
  });

  let spanningCells = getSpanningCells();
  spanningCells.forEach(cell => {
    spanningCellResizeObserver.observe(cell);
  });
}

function setStickyHeaderRowOffsets() {
  if (tableRef.value == null)
    return;

  let headerRows = [ ... (tableRef.value.querySelectorAll("tr.thead") ?? []) ];
  let topOffset = 0;
  headerRows.forEach(row => {
    let headerCells = [ ... row.querySelectorAll("th") ];
    headerCells.forEach(cell => {
      cell.style.top = `${topOffset}px`;
    });
    topOffset += row.offsetHeight;
  });

  let inlineHeaderRows = [ ... (tableRef.value.querySelectorAll("tr.inlineColumnHeaderRow") ?? []) ];
  let inlineHeaderLevels = [ ... new Set(inlineHeaderRows.map(row => Number(row.dataset.headerLevel)).filter(level => !isNaN(level))) ].sort((a, b) => a - b);
  let topOffsetByLevel = new Map();
  inlineHeaderLevels.forEach(level => {
    topOffsetByLevel.set(level, topOffset);
    let firstRowAtLevel = inlineHeaderRows.find(row => Number(row.dataset.headerLevel) == level);
    topOffset += firstRowAtLevel?.offsetHeight ?? 0;
  });

  inlineHeaderRows.forEach(row => {
    let level = Number(row.dataset.headerLevel);
    let rowTopOffset = topOffsetByLevel.get(level) ?? topOffset;
    let headerCells = [ ... row.querySelectorAll("th, td") ];
    headerCells.forEach(cell => {
      cell.style.top = `${rowTopOffset}px`;
    });
  });
}

function setHeaderColumnOffsets() {
  // Early return if we don't have the tableRef yet. This prevents an error occuring on rare occasions when tableRef 
  // doesn't yet have a value. Not sure how this function is being called when that's the case though.
  if (tableRef.value == null)
    return;

  let rows = [ ... (tableRef.value.querySelectorAll("tr.tbody") ?? []) ];
  let hdrCells = rows.flatMap(row => [...row.querySelectorAll("th.rowHeader")]);

  // Compute the row and column index for each <th> element.
  let hdrCellsGrid = [];
  for (let i = 0; i < rows.length; i++) hdrCellsGrid.push([]);
  hdrCells.forEach(hdrCell => {
    let rowIndex = rows.findIndex(row => [...row.querySelectorAll("th")].includes(hdrCell));
    hdrCell.rowIndex = rowIndex;
    hdrCell.columnIndex = hdrCellsGrid[rowIndex].length;
    hdrCellsGrid[rowIndex].push(hdrCell);
    for (let i = 0; i < (hdrCell.colSpan - 1); i++) hdrCellsGrid[rowIndex].push(null);          // Fill to the right.
    for (let i = 0; i < (hdrCell.rowSpan - 1); i++) hdrCellsGrid[rowIndex + 1 + i].push(null);  // Fill below.
  });

  // Find the first <th> in each column that has colSpan == 1. Those <th> have the column's width.
  let hdrCellsSingleColSpan = hdrCells.filter(hdrCell => hdrCell.colSpan == 1);
  let hdrCellsWithColumnWidth = [];
  for (let i = 0; i < maxHeaderLength.value; i++) {
    hdrCellsWithColumnWidth.push(hdrCellsSingleColSpan.find(hdrCell => hdrCell.columnIndex == i));
  }

  let rowHeadersWidth = sumBy(hdrCellsWithColumnWidth, cell => cell?.offsetWidth ?? 0);
  scrollContainerRef.value?.style.setProperty("--row-headers-width", `${rowHeadersWidth}px`);

  // Compute each column's distance from table's left edge.
  let columnOffsets = hdrCellsWithColumnWidth.map((hdrCell, index) => {
    let precedingCells = hdrCellsWithColumnWidth.slice(0, index);
    let offset = sumBy(precedingCells, cell => cell.offsetWidth);
    return offset;
  });

  // Set each header cell's .left property according to the corresponding column offset computed above.
  hdrCells.forEach(hdrCell => {
    hdrCell.style.left = `${columnOffsets[hdrCell.columnIndex]}px`;
    hdrCell.classList.toggle("hasLeftBorder", hdrCell.columnIndex > 0);
  });
};

function setSpanningCellContentOffsets() {
  if (tableRef.value == null || scrollContainerRef.value == null)
    return;

  let scrollContainerRect = scrollContainerRef.value.getBoundingClientRect();
  let rowHeadersWidth = parseFloat(getComputedStyle(scrollContainerRef.value).getPropertyValue("--row-headers-width")) || 0;
  let visibleContentLeft = scrollContainerRect.left + rowHeadersWidth;
  let visibleContentRight = scrollContainerRect.right;
  let spanningCells = getSpanningCells();
  spanningCells.forEach(cell => {
    let content = cell.querySelector(":scope > span.columnHeaderContent, :scope > div.cellDiv");
    if (content == null)
      return;

    cell.style.setProperty("--spanning-cell-visible-center-offset", "0px");

    let cellRect = cell.getBoundingClientRect();
    let contentRect = content.getBoundingClientRect();
    let cellStyle = getComputedStyle(cell);
    let cellPaddingLeft = parseFloat(cellStyle.paddingLeft) || 0;
    let cellPaddingRight = parseFloat(cellStyle.paddingRight) || 0;
    let visibleLeft = Math.max(cellRect.left, visibleContentLeft);
    let visibleRight = Math.min(cellRect.right, visibleContentRight);
    let visibleWidth = Math.max(0, visibleRight - visibleLeft);
    let visibleCenter = (visibleWidth > 0)
      ? visibleLeft + visibleWidth / 2 - cellRect.left
      : cellRect.width / 2;

    let visibleLeftInCell = Math.max(cellPaddingLeft, visibleLeft - cellRect.left);
    let visibleRightInCell = Math.min(cellRect.width - cellPaddingRight, visibleRight - cellRect.left);
    let naturalContentLeft = contentRect.left - cellRect.left;
    let naturalContentRight = contentRect.right - cellRect.left;
    let naturalContentCenter = (naturalContentLeft + naturalContentRight) / 2;
    let minOffset = visibleLeftInCell - naturalContentLeft;
    let maxOffset = visibleRightInCell - naturalContentRight;
    let centerOffset = visibleCenter - naturalContentCenter;
    if (minOffset <= maxOffset) {
      centerOffset = Math.max(minOffset, Math.min(centerOffset, maxOffset));
    }
    else {
      centerOffset = (visibleCenter <= naturalContentCenter) ? minOffset : maxOffset;
    }

    cell.style.setProperty("--spanning-cell-visible-center-offset", `${centerOffset}px`);
    content.style.opacity = (visibleWidth > 0) ? "1" : "0";
  });
}

function queueSpanningCellContentPositionUpdate() {
  if (spanningCellPositionAnimationFrame != null)
    return;

  spanningCellPositionAnimationFrame = requestAnimationFrame(() => {
    spanningCellPositionAnimationFrame = null;
    setSpanningCellContentOffsets();
  });
}

function updateStickyOffsets() {
  setStickyHeaderRowOffsets();
  setHeaderColumnOffsets();
  setSpanningCellContentOffsets();
}

watch([ firstRender, rows, columnsByLevel, showingDesktopUi ], async () => {
  await promiseTimeout();
  updateStickyOffsets();
  resetSpanningCellObservers();
});

onMounted(() => {
  window.addEventListener("resize", updateStickyOffsets);
  resetSpanningCellObservers();
  document.fonts.ready.then(() => updateStickyOffsets());
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateStickyOffsets);
  spanningCellResizeObserver?.disconnect();
  if (spanningCellPositionAnimationFrame != null)
    cancelAnimationFrame(spanningCellPositionAnimationFrame);
  resetDragScrollState();
});
</script>

<style>
.p-toast-detail {
  margin-top: 5px;
}
</style>

<style scoped>
.MultiColumnTable {
  --border-style: 1px solid #888;
  --table-border-bottom-style: 1px solid #888;
  background: white;
  overscroll-behavior: none; /* Overscroll causes the entire page to slide around too much when there isn't much scroll required. */
  touch-action: none;
}
.desktop .MultiColumnTable {
  border-radius: 10px;
  border: var(--border-style);
}
.desktop .MultiColumnTable::-webkit-scrollbar {
  width: 2px;
  height: 2px;
  background: transparent;
}
.desktop .MultiColumnTable::-webkit-scrollbar-track {
  border-radius: 10px;
}
.desktop .MultiColumnTable::-webkit-scrollbar-thumb {
  border-radius: 10px;
  background: gray;
}
.MultiColumnTable.dragging {
  cursor: grabbing;
  user-select: none;
}
table {
  width: 100%;
}
th {
  font-weight: 600;
  background: white;
}
th, :deep(td) {
  padding: 10px;
}
tr.tbody th {
  height: 60px;
  text-align: center;
  z-index: 10;
}
:deep(td) {
  text-align: center;
}
:deep(td.showingPopover) {
  background: rgba(30, 143, 255, 0.2);
}
:deep(td.isCellSelected:not(.showingPopover)) {
  background: rgba(30, 143, 255, 0.07);
}
/* Sticky headers */
table {
  border-collapse: separate; /* borders don't stay attached to sticky headers otherwise */
  border-spacing: 0;
}
tr.thead th {
  position: sticky;
  top: 0;
  z-index: 10;
}

tr.tbody th.rowHeader {
  position: sticky;
  left: 0;
}
tr.tbody th.rowHeader.hasLeftBorder {
  border-left: var(--border-style);
}
tr.tbody th.rowHeader:last-of-type {
  border-right: var(--border-style);
}
tr.thead th.tableTitle {
  position: sticky;
  top: 0;
  left: 0;
  border-right: var(--border-style);
  
  padding-left: 30px;
  padding-right: 30px;
  font-size: large;
  font-weight: bold;
  color: var(--primary-700);
  z-index: 20;

}
tr.thead th:not(.tableTitle):not(.firstColumnHeader) {
  border-left: var(--border-style);
}
tr.inlineColumnHeaderRow th,
tr.inlineColumnHeaderRow td {
  position: sticky;
  padding: 3px 8px;
  text-align: center;
  border-bottom: var(--border-style);
  z-index: 10;
}
tr.inlineColumnHeaderRow > :not(:first-child) {
  border-left: var(--border-style);
}
tr.inlineColumnHeaderRow th.subColumnSpacer {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 15;
  border-right: var(--border-style);
}
tr.inlineColumnHeaderRow th:nth-child(2) {
  border-left: 0;
}
th, :deep(td) {
  border-bottom: var(--border-style);
}
:deep(td):not(:first-of-type) {
  border-left: var(--border-style);
}

/* Keep merged-cell content centered within the visible portion of the cell. */
:deep(td.spanningCell),
th.spanningColumnHeader {
  position: relative;
  overflow: hidden;
}
:deep(td.spanningCell > div.cellDiv),
th.spanningColumnHeader > span.columnHeaderContent {
  position: relative;
  transform: translateX(var(--spanning-cell-visible-center-offset, 0px));
  display: inline-block;
  padding-left: 8px;
  padding-right: 8px;
  white-space: nowrap;
}

tr.tbody:last-child th, tr.tbody:last-child :deep(td) {
  border-bottom: 0px;
}

.desktop th {
  color: #263140;
  background: whitesmoke;
}
.desktop tr.tbody th.rowHeader {
  height: initial;
  white-space: nowrap;
}
.desktop th, .desktop :deep(td) {
  padding: 8px;
  padding-left: 15px;
  padding-right: 15px;
}
.desktop tr.thead th {
  padding: 3px;
}

.mobile .MultiColumnTable::-webkit-scrollbar { display: none; }
.mobile .MultiColumnTable {
  --border-style: 1px solid #cbd5e1;
  --table-border-bottom-style: 1px solid #cbd5e1;
}
.mobile th {
  background: #f9f9fa;
}
.mobile tr.tbody th.rowHeader {
  text-align: left;
}

.mobile tr.thead:first-child th:first-child {
  border-top-left-radius: 0;
}
.mobile tr.thead:first-child th:last-child {
  border-top-right-radius: 0;
}
.mobile tr.tbody th.bottomLeft {
  border-bottom-left-radius: 0;
}
.mobile tr.tbody:last-child :deep(td:last-child) {
  border-bottom-right-radius: 0;
}
</style>